import { useCatalogStore } from "application/contexts/catalog/CatalogStore";
import { useKiosk } from "application/contexts/kiosk/KioskContext";
import { useScreenStore } from "application/contexts/screen/ScreenStore";
import { useSession } from "application/contexts/session/SessionContext";
import { useUi } from "application/contexts/ui/UIContext";
import { ErrorCodeType } from "application/models/IError";
import { IActionModalHandler } from "components/actionModal/ActionModal";
import { randomIntFromInterval } from "corss-cutting/numberFunctions";
import { useAppInsights } from "Infrastructure/repositories/appInsights/AppInsights";
import { CalcServiceFeeUseCase } from "modules/cart/application/useCases/CalcServiceFeeUseCase";
import { ICartContext } from "modules/cart/domain/interfaces/IUseCaseCart";
import { ICartItem } from "modules/cart/domain/models/ICart";
import { useCartStore } from "modules/cart/infra/store/CartStore";
import { IOrder, ProdutoTipo } from "modules/order/domain/models/IOrder";
import { IPaymentResult } from "modules/payment/presentation/PaymentProvider";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { GetError } from "utils/GetError";
import { roundValue } from "utils/utils";
import { v4 } from "uuid";
import { toast as toastify } from "react-toastify";
import { ReactComponent as AddProduct } from "../../../../assets/icons/addProduct.svg";
import styles from "./CartView.module.scss";
import "./ProductToast.css";
import UserRegisterService from "modules/cpfRegister/service/UserRegisterService";
import { HttpRepository } from "Infrastructure/repositories/api/_HttpRepository";
import { UserValidateUseCase } from "modules/cart/application/useCases/UserValidateUseCase";
import { useTranslation } from "i18n/useTranslation";

const UseCartView = (
  onSendOrder: (order: IOrder) => Promise<IPaymentResult>
) => {
  const { kiosk } = useKiosk();
  const { getConfigurationProduct } = useCatalogStore();
  const { addLog } = useAppInsights();
  const { toast, showLoading, hideLoading } = useUi();
  const { verifySessionIsOpen, sessaoIdPos } = useSession();
  const { t, getProductName } = useTranslation();

  const clearCartRef = useRef<IActionModalHandler>(null);

  const [
    cartItems,
    clearCart,
    lastProductAdded,
    openLastProductToast,
    clearLastProduct,
    totals,
    configCart,
    setNewCart,
    showVerifyMajorityModal,
    tempMajorityProduct,
    addProductCart,
    setAdultHood,
    setShowVerifyMajorityModal,
    clearTempMajorityProduct,
    showCart,
    setShowCart,
    suggestionProducts,
  ] = useCartStore((state: ICartContext) => [
    state.cartItems,
    state.clearCart,
    state.lastProductAdded,
    state.openLastProductToast,
    state.clearLastProduct,
    state.totals,
    state.configCart,
    state.setNewCart,
    state.showVerifyMajorityModal,
    state.tempMajorityProduct,
    state.addProductCart,
    state.setAdultHood,
    state.setShowVerifyMajorityModal,
    state.clearTempMajorityProduct,
    state.showCart,
    state.setShowCart,
    state.suggestionProducts,
  ]);

  const { resolution } = useScreenStore();

  const [openOrderToGoModal, setOpenOrderToGoModal] = useState(false);
  const [openTooltip, setOpenTooltip] = useState(false);
  const [showCpfInput, setShowCpfInput] = useState(false);

  useEffect(() => {
    if (openLastProductToast && lastProductAdded) {
      toastify(
        <div className={styles.addedProductToast}>
          <AddProduct/>
          <div>
            <b>
              {getProductName(lastProductAdded)}
            </b>
            <span>{t("cart_view.toast.cart_added")}</span>
          </div>
        </div>,
        {
          type: "default",
          position: "top-right",
          autoClose: 2000,
          hideProgressBar: true,
          closeOnClick: true,
          pauseOnHover: false,
          draggable: false,
          progress: undefined,
          onClose: () => clearLastProduct(),
          closeButton: false,
          className: "addedProductToast",
        }
      );
    }
  }, [clearLastProduct, getProductName, kiosk, lastProductAdded, openLastProductToast, t]);

  const toogleCpfInput = () => {
    setShowCpfInput((prev) => !prev);
  };

  const handleTooltipToggle = useCallback(() => {
    setOpenTooltip((prev) => !prev);
  }, []);

  const cartBarHandle = useCallback(() => {
    if (showCart) {
      setOpenTooltip(false);
    }
    setShowCart(!showCart);
  }, [setShowCart, showCart]);

  const totalServiceFeeValue = CalcServiceFeeUseCase(cartItems, kiosk, totals);

  const serviceFeeProduct = useMemo(() => {
    if (totalServiceFeeValue > 0 && kiosk) {
      return {
        id: v4(),
        name: "Taxa de serviço de " + kiosk.serviceRatePercent * 100 + "%",
        category: "",
        price: totalServiceFeeValue,
        realPrice: totalServiceFeeValue,
        totalPrice: totalServiceFeeValue,
        description: "",
        productPrice: totalServiceFeeValue,
        productId: getConfigurationProduct(ProdutoTipo.TaxaServico)?.id ?? "",
        productType: ProdutoTipo.TaxaServico,
        quantity: 1,
        imageUrl: "",
        thumbnailUrl: "",
        barcode: "",
        hideCatalog: false,
        orderComposition: [],
        compositions: [],
        printerName: null,
        translations: [],
      } as ICartItem;
    } else {
      return null;
    }
  }, [getConfigurationProduct, kiosk, totalServiceFeeValue]);

  const totalWithServiceFee = useMemo(
    () => roundValue(totals.totalPrice + totalServiceFeeValue),
    [totalServiceFeeValue, totals.totalPrice]
  );

  const sendOder = useCallback(
    async (observation?: string) => {
      const friendlyId = randomIntFromInterval(1, 9999);
      const id = v4();
      setNewCart(id);
      const order = {
        createdAt: new Date().toISOString(),
        id: id,
        sessionId: sessaoIdPos,
        orderItems: serviceFeeProduct
          ? [...cartItems, serviceFeeProduct]
          : cartItems,
        observation: observation,
        totalValue: totalWithServiceFee,
        friendlyId: kiosk?.kioskName.replace(/\D/gm, "") + " " + friendlyId,
      } as IOrder;
      addLog("Cart Confirm", { order });
      try {
        await onSendOrder(order);
      } catch (error) {
        GetError(
          error,
          "sendOder",
          "UseCartView",
          undefined,
          ErrorCodeType.PAYMENT
        );
      }
    },
    [
      setNewCart,
      serviceFeeProduct,
      cartItems,
      totalWithServiceFee,
      kiosk,
      onSendOrder,
      addLog,
      sessaoIdPos
    ]
  );

  const onConfirmOrder = useCallback(async () => {
    showLoading();
    const result = await verifySessionIsOpen();
    hideLoading();

    if (!result) {
      return;
    }

    if (cartItems.length === 0) {
      toast(t("cart_view.toast.empty_cart"), "error");
    } else if (kiosk?.orderToGo) {
      setOpenOrderToGoModal(true);
    } else {
      sendOder();
    }
  }, [cartItems.length, hideLoading, kiosk, sendOder, showLoading, toast, verifySessionIsOpen, t]);

  const handleOrderToGo = useCallback(
    (obs?: string) => {
      setOpenOrderToGoModal(false);
      sendOder(obs);
    },
    [sendOder]
  );

  const onPressSimMaioridade = async (document?: string) => {
    const httpRepository = HttpRepository();
    const userRegisterService = UserRegisterService(httpRepository);
    try {
      showLoading();
      setShowCpfInput(false);
      const isAdult = await UserValidateUseCase(
        userRegisterService,
        kiosk?.localId ?? "",
        document ?? ""
      );
      if (!isAdult) {
        toast(t("cart_view.toast.not_adult"), "error");
        return;
      }
    } catch (error) {
      toast(t("cart_view.toast.failed"), "error");
      return;
    } finally {
      hideLoading();
    }

    if (tempMajorityProduct) {
      setShowCpfInput(false);
      setShowVerifyMajorityModal(false);
      setAdultHood(true);
      addProductCart(
        tempMajorityProduct.product,
        tempMajorityProduct.quantity,
        tempMajorityProduct.orderCompositions
      );
    }
  };

  const onPressNaoMaioridade = () => {
    setAdultHood(false);
    clearTempMajorityProduct();
    setShowVerifyMajorityModal(false);
    setShowCpfInput(false);
  };

  const handleClearCart = useCallback(async () => {
    try {
      await clearCartRef.current?.open();
      clearCart();
    } catch (error) {}
  }, [clearCart]);

  const handleCloseOrderToGoModal = useCallback(() => {
    setOpenOrderToGoModal(false);
  }, []);

  return {
    lastProductAdded,
    openLastProductToast,
    clearLastProduct,
    cartBarHandle,
    cartItems,
    showCart,
    configCart,
    kiosk,
    totalWithServiceFee,
    handleClearCart,
    totalServiceFeeValue,
    onConfirmOrder,
    openOrderToGoModal,
    showVerifyMajorityModal,
    onPressSimMaioridade,
    onPressNaoMaioridade,
    sendOder,
    setOpenOrderToGoModal,
    setShowVerifyMajorityModal,
    resolution,
    openTooltip,
    handleTooltipToggle,
    clearCartRef,
    handleCloseOrderToGoModal,
    handleOrderToGo,
    suggestionProducts,
    showCpfInput,
    toogleCpfInput,
    t
  };
};

export default UseCartView;
